import {
  Alert, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Cancel, DeleteOutline, Edit, Save } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '../../../shared/api';
import type { Class, Subject, Teacher, TimetableEntry } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { DAYS_OF_WEEK, PERIODS } from '../../../shared/constants';

const SESSION_ID = 'sess_2025_26';
const cellKey = (day: number, period: number) => `${day}-${period}`;

type Draft = Omit<TimetableEntry, 'id' | 'classId'>;

export default function TimetablePage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'ADMIN' || user?.role === 'PRINCIPAL';
  const qc = useQueryClient();

  const [classId, setClassId] = useState(user?.classId ?? '');
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Map<string, Draft>>(new Map());
  const [openCell, setOpenCell] = useState<{ day: number; period: number } | null>(null);

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const subjectsQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });
  const teachersQ = useQuery({ queryKey: ['teachers'], queryFn: async () => (await api.get<Teacher[]>('/admin/teachers')).data });
  const ttQ = useQuery({
    queryKey: ['timetable', classId],
    queryFn: async () => (await api.get<TimetableEntry[]>('/timetable', { params: { classId } })).data,
    enabled: !!classId,
  });

  const subjMap = useMemo(() => new Map((subjectsQ.data ?? []).map((s) => [s.id, s])), [subjectsQ.data]);
  const teachMap = useMemo(() => new Map((teachersQ.data ?? []).map((t) => [t.id, t])), [teachersQ.data]);

  // when class changes or fresh data arrives, sync drafts to server data and exit edit mode
  useEffect(() => {
    const m = new Map<string, Draft>();
    (ttQ.data ?? []).forEach((e) => {
      m.set(cellKey(e.dayOfWeek, e.period), {
        sessionId: e.sessionId, dayOfWeek: e.dayOfWeek, period: e.period,
        startTime: e.startTime, endTime: e.endTime,
        subjectId: e.subjectId, teacherId: e.teacherId,
      });
    });
    setDrafts(m);
    setEditing(false);
    setOpenCell(null);
  }, [ttQ.data, classId]);

  const save = useMutation({
    mutationFn: async () => {
      const entries = Array.from(drafts.values());
      return (await api.put('/timetable', { classId, entries })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timetable'] });
      setEditing(false);
    },
  });

  const periods = useMemo(() => PERIODS.map((p) => p.value), []);

  const subjectColor = (subjId: string) => {
    const palette = ['#1E3A8A', '#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#A855F7'];
    let h = 0; for (let i = 0; i < subjId.length; i++) h = (h * 31 + subjId.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  };

  const handleCellClick = (day: number, period: number) => {
    if (!editing) return;
    setOpenCell({ day, period });
  };

  const upsertCell = (day: number, period: number, draft: Draft) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(cellKey(day, period), draft);
      return next;
    });
  };

  const clearCell = (day: number, period: number) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.delete(cellKey(day, period));
      return next;
    });
  };

  const handleEditToggle = () => {
    if (editing) {
      // cancel: re-sync from server
      const m = new Map<string, Draft>();
      (ttQ.data ?? []).forEach((e) => {
        m.set(cellKey(e.dayOfWeek, e.period), {
          sessionId: e.sessionId, dayOfWeek: e.dayOfWeek, period: e.period,
          startTime: e.startTime, endTime: e.endTime,
          subjectId: e.subjectId, teacherId: e.teacherId,
        });
      });
      setDrafts(m);
    }
    setEditing(!editing);
  };

  const dirty = useMemo(() => {
    const server = new Map<string, Draft>();
    (ttQ.data ?? []).forEach((e) => server.set(cellKey(e.dayOfWeek, e.period), {
      sessionId: e.sessionId, dayOfWeek: e.dayOfWeek, period: e.period,
      startTime: e.startTime, endTime: e.endTime,
      subjectId: e.subjectId, teacherId: e.teacherId,
    }));
    if (server.size !== drafts.size) return true;
    const draftEntries = Array.from(drafts.entries());
    for (let i = 0; i < draftEntries.length; i++) {
      const [k, d] = draftEntries[i];
      const s = server.get(k);
      if (!s || s.subjectId !== d.subjectId || s.teacherId !== d.teacherId
          || s.startTime !== d.startTime || s.endTime !== d.endTime) return true;
    }
    return false;
  }, [drafts, ttQ.data]);

  const editingDraft = openCell ? drafts.get(cellKey(openCell.day, openCell.period)) : null;

  return (
    <Box>
      <PageHeader
        title="Timetable"
        subtitle={editing ? 'Editing — click any cell to update.' : 'Weekly schedule for the class.'}
        action={canEdit && classId && (
          <Stack direction="row" spacing={1.5}>
            {editing && (
              <Button
                variant="contained" color="primary" startIcon={<Save />}
                onClick={() => save.mutate()} disabled={save.isPending || !dirty}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            )}
            <Button
              variant={editing ? 'outlined' : 'contained'}
              color={editing ? 'inherit' : 'primary'}
              startIcon={editing ? <Cancel /> : <Edit />}
              onClick={handleEditToggle}
            >
              {editing ? 'Cancel' : 'Edit timetable'}
            </Button>
          </Stack>
        )}
      />

      {save.isError && <Alert severity="error" sx={{ mb: 2 }}>{extractError(save.error)}</Alert>}
      {save.isSuccess && !editing && <Alert severity="success" sx={{ mb: 2 }}>Timetable saved.</Alert>}
      {editing && dirty && <Alert severity="warning" sx={{ mb: 2 }}>You have unsaved changes.</Alert>}

      <Card sx={{ p: 2, mb: 2 }}>
        <TextField
          select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}
          sx={{ minWidth: 220 }} disabled={editing}
          helperText={editing ? 'Save or cancel before switching class' : ''}
        >
          {(classesQ.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
        </TextField>
      </Card>

      {!classId ? (
        <Card sx={{ p: 6, textAlign: 'center' }}><Typography color="text.secondary">Select a class.</Typography></Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'primary.dark', color: '#fff' }}>Period</TableCell>
                {DAYS_OF_WEEK.map((d) => (
                  <TableCell key={d.value} sx={{ fontWeight: 700, bgcolor: 'primary.dark', color: '#fff' }}>{d.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((p) => (
                <TableRow key={p}>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#F1F5F9', whiteSpace: 'nowrap' }}>P{p}</TableCell>
                  {DAYS_OF_WEEK.map((d) => {
                    const draft = drafts.get(cellKey(d.value, p));
                    const interactive = editing;
                    if (!draft) {
                      return (
                        <TableCell
                          key={d.value}
                          onClick={() => handleCellClick(d.value, p)}
                          sx={{
                            bgcolor: '#FAFAFA',
                            cursor: interactive ? 'pointer' : 'default',
                            color: 'text.disabled', textAlign: 'center',
                            '&:hover': interactive ? { bgcolor: '#F1F5F9' } : undefined,
                          }}
                        >
                          {interactive ? <Add fontSize="small" /> : '—'}
                        </TableCell>
                      );
                    }
                    const subj = subjMap.get(draft.subjectId);
                    const t = teachMap.get(draft.teacherId);
                    const color = subjectColor(draft.subjectId);
                    return (
                      <TableCell
                        key={d.value}
                        onClick={() => handleCellClick(d.value, p)}
                        sx={{
                          p: 1, verticalAlign: 'top', minWidth: 130,
                          cursor: interactive ? 'pointer' : 'default',
                          '&:hover': interactive ? { bgcolor: '#F8FAFC' } : undefined,
                        }}
                      >
                        <Box sx={{
                          p: 1, borderLeft: `4px solid ${color}`, bgcolor: `${color}10`, borderRadius: 1,
                          outline: interactive ? '1px dashed transparent' : 'none',
                          '&:hover': interactive ? { outline: `1px dashed ${color}` } : undefined,
                        }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{draft.startTime} – {draft.endTime}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{subj?.name ?? draft.subjectId}</Typography>
                          <Typography variant="caption" color="text.secondary">{t ? `${t.firstName} ${t.lastName}` : draft.teacherId}</Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CellEditor
        open={!!openCell}
        day={openCell?.day ?? 1}
        period={openCell?.period ?? 1}
        existing={editingDraft ?? null}
        subjects={subjectsQ.data ?? []}
        teachers={teachersQ.data ?? []}
        onSave={(d) => { if (openCell) upsertCell(openCell.day, openCell.period, d); setOpenCell(null); }}
        onClear={() => { if (openCell) clearCell(openCell.day, openCell.period); setOpenCell(null); }}
        onClose={() => setOpenCell(null)}
      />
    </Box>
  );
}

function CellEditor({
  open, day, period, existing, subjects, teachers, onSave, onClear, onClose,
}: {
  open: boolean; day: number; period: number;
  existing: Draft | null;
  subjects: Subject[]; teachers: Teacher[];
  onSave: (draft: Draft) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const periodDefault = PERIODS.find((p) => p.value === period);
  const [subjectId, setSubjectId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  // re-init form whenever the dialog opens for a different cell
  useEffect(() => {
    if (!open) return;
    setSubjectId(existing?.subjectId ?? '');
    setTeacherId(existing?.teacherId ?? '');
    setStartTime(existing?.startTime ?? periodDefault?.startTime ?? '08:00');
    setEndTime(existing?.endTime ?? periodDefault?.endTime ?? '08:45');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, day, period]);

  const dayLabel = DAYS_OF_WEEK.find((d) => d.value === day)?.label ?? '';
  const valid = subjectId && teacherId && startTime && endTime;

  const handleSave = () => {
    if (!valid) return;
    onSave({
      sessionId: SESSION_ID, dayOfWeek: day, period,
      subjectId, teacherId, startTime, endTime,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {dayLabel} · Period {period}
        {existing && (
          <Tooltip title="Clear this slot">
            <IconButton onClick={onClear} sx={{ position: 'absolute', right: 12, top: 12 }} color="error" size="small">
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Subject" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} required>
            {subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField select label="Teacher" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} required>
            {teachers.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({t.employeeId})</Typography></MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Start time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="End time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid}>
          {existing ? 'Update slot' : 'Add slot'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
